import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Typography, useViewport } from '@chilecompra/react-kit/components'
import { onContractFilesThunk, onContractFilesDetailThunk } from './ContractFiles.actions'
import IconButton from '../../components/Buttons/IconButton'
import { Card, Chip, GridContainer, GridItem, Label, Link, Root, Strong } from './ContractFiles.styles'

/**
 * The ContractFiles' container.
 */
const ContractFiles = forwardRef((props, ref) => {
  const { year, onOpen } = props
  const dispatch = useDispatch()
  const { idEntity } = useSelector(state => state.buyerSection)
  const {
    data: { Garanties, Sanctions, Finished },
    details: { tableStructure },
    fetching: loading
  } = useSelector(state => state.contractFiles)
  const { size } = useViewport()
  const [showTable, setShowTable] = useState(false)
  const [category, setCategory] = useState('')
  const finishedDataTableRef = useRef()
  const sanctionsDataTableRef = useRef()
  const warrantiesDataTableRef = useRef()
  const { loading: initialLoading } = useSelector(state => state.topNavbar)
  const handleContractDetails = type => () => {
    onOpen('files')
    setShowTable(true)
    setCategory(type)
  }
  const handleReturn = () => {
    onOpen(null)
    setShowTable(false)
    setCategory('')
  }

  useImperativeHandle(ref, () => ({
    close: () => {
      setShowTable(false)
      setCategory('')
    }
  }))

  useEffect(() => {
    finishedDataTableRef.current?.setPage(1)
    sanctionsDataTableRef.current?.setPage(1)
    warrantiesDataTableRef.current?.setPage(1)
  }, [year])

  useEffect(() => {
    if (!idEntity || !showTable || !category) {
      return
    }

    dispatch(onContractFilesDetailThunk(idEntity, year, category))
  }, [category, idEntity, showTable, year])

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onContractFilesThunk(idEntity, year))
  }, [initialLoading, idEntity, year])

  return (
    <Root>
      <GridContainer justifyContent="center" spacing={1}>
        <GridItem align="left" xs={12}>
          <Typography
            fontSize="14px"
            fontWeight={category ? 'regular' : 'bold'}
            margin="24px 0 0"
            padding="0 0 16px"
            variant="body2"
          >
            Principales indicadores {category ? <strong> / {category}</strong> : null}
          </Typography>
          {category ? (
            <IconButton type="back" onClick={handleReturn} fontWeight="bold" fontSize="14px">
              <Label>Volver a los indicadores</Label>
            </IconButton>
          ) : null}
        </GridItem>
        {!showTable && (
          <>
            <GridItem align="left" md={4} padding={size.isDownToMedium ? '0 16px' : '0 8px 0 0'} xs={10}>
              {Garanties.value === 0 ? (
                <Card enableHover color="blank" elevation="1" rows={1}>
                  <Typography fontSize="13px" padding="15px 0" variant="body2">
                    Sin fichas de contrato con garantías
                  </Typography>
                </Card>
              ) : (
                <Card enableHover color="blank" elevation="1" onClick={handleContractDetails('garantias')}>
                  <Typography fontSize="13px" padding="0 0 8px" variant="body2">
                    <Strong>{Garanties.value}</Strong>Fichas de contrato con garantías
                  </Typography>
                  <Typography color="primary" fontSize="12px" variant="body2">
                    Ver detalle
                  </Typography>
                </Card>
              )}
            </GridItem>
            <GridItem align="left" md={4} padding={size.isDownToMedium ? '0 16px' : '0 8px 0 0'} xs={10}>
              {Sanctions.value === 0 ? (
                <Card enableHover color="blank" elevation="1" rows={1}>
                  <Typography fontSize="13px" padding="15px 0" variant="body2">
                    Sin fichas de contrato con sanciones
                  </Typography>
                </Card>
              ) : (
                <Card enableHover color="blank" elevation="1" onClick={handleContractDetails('sanciones')}>
                  <Typography fontSize="13px" padding="0 0 8px" variant="body2">
                    <Strong>{Sanctions.value}</Strong> Fichas de contrato con sanciones
                  </Typography>
                  <Typography color="primary" fontSize="12px" variant="body2">
                    Ver detalle
                  </Typography>
                </Card>
              )}
            </GridItem>
            <GridItem align="left" md={4} padding={size.isDownToMedium ? '0 16px' : '0 0 0 8px'} xs={10}>
              {Finished.value === 0 ? (
                <Card enableHover color="blank" elevation="1" rows={1}>
                  <Typography fontSize="13px" padding="15px 0" variant="body2">
                    Sin fichas de contrato en evaluación
                  </Typography>
                </Card>
              ) : (
                <Card enableHover color="blank" elevation="1" onClick={handleContractDetails('terminados')}>
                  <Typography fontSize="13px" padding="0 0 8px" variant="body2">
                    <Strong>{Finished.value}</Strong> Contratos terminados en evaluación
                  </Typography>
                  <Typography color="primary" fontSize="12px" variant="body2">
                    Ver detalle
                  </Typography>
                </Card>
              )}
            </GridItem>
          </>
        )}
        {showTable && category === 'garantias' && (
          <GridItem minHeight="300px" xs={12}>
            <DataTable
              headers={[
                { label: 'ID ficha de contrato', value: 'contractCode' },
                { label: 'Número de garantía', value: 'warrantyNumber' },
                { label: 'Tipo de garantía', value: 'warrantyType' },
                { label: 'Estado de garantía', value: 'warrantyStatus' },
                { label: 'Tipo de documento', value: 'warrantyDocumentType' }
              ]}
              headersCellsProps={[
                { enableFilter: true, enableSort: true, width: '20%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '30%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '20%' }
              ]}
              headersDecorators={[
                () => (
                  <>
                    ID ficha de
                    <br />
                    contrato
                  </>
                ),
                () => (
                  <>
                    Número de
                    <br />
                    garantía
                  </>
                ),
                () => (
                  <>
                    Tipo de
                    <br />
                    garantía
                  </>
                ),
                () => (
                  <>
                    Estado de
                    <br />
                    garantía
                  </>
                ),
                () => (
                  <>
                    Tipo de
                    <br />
                    documento
                  </>
                )
              ]}
              initialRows={6}
              loading={loading}
              records={tableStructure}
              recordsDecorators={[
                ({ contractCode, linkContrato }) => (
                  <Link href={linkContrato} rel="noopener noreferrer" target="_blank">
                    {contractCode === '0' ? <Chip label="No Existe" /> : contractCode}
                  </Link>
                ),
                ({ warrantyNumber }) => warrantyNumber,
                ({ warrantyType }) => warrantyType,
                ({ warrantyStatus }) => warrantyStatus,
                ({ warrantyDocumentType }) => warrantyDocumentType
              ]}
              ref={warrantiesDataTableRef}
            />
          </GridItem>
        )}
        {showTable && category === 'sanciones' && (
          <GridItem minHeight="300px" xs={12}>
            <DataTable
              headers={[
                { label: 'ID ficha de contrato', value: 'contractCode' },
                { label: 'Número de sanción', value: 'sanctionID' },
                { label: 'Tipo de sanción', value: 'sanctionType' },
                { label: 'Fecha de sanción', value: 'sanctionDate' },
                { label: 'Estado de sanción', value: 'sanctionStatus' }
              ]}
              headersCellsProps={[
                { enableFilter: true, enableSort: true, width: '20%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '20%' },
                { enableFilter: true, enableSort: true, width: '30%' }
              ]}
              headersDecorators={[
                () => (
                  <>
                    ID ficha de
                    <br />
                    contrato
                  </>
                ),
                () => (
                  <>
                    Número de
                    <br />
                    sanción
                  </>
                ),
                () => (
                  <>
                    Tipo de
                    <br />
                    sanción
                  </>
                ),
                () => (
                  <>
                    Fecha de
                    <br />
                    sanción
                  </>
                ),
                () => (
                  <>
                    Estado de
                    <br />
                    sanción
                  </>
                )
              ]}
              initialRows={6}
              loading={loading}
              records={tableStructure}
              recordsDecorators={[
                ({ contractCode, linkContrato }) => (
                  <Link href={linkContrato} rel="noopener noreferrer" target="_blank">
                    {contractCode === '0' ? <Chip label="No Existe" /> : contractCode}
                  </Link>
                ),
                ({ sanctionID }) => sanctionID,
                ({ sanctionType }) => sanctionType,
                ({ sanctionDate }) => sanctionDate,
                ({ sanctionStatus }) => sanctionStatus
              ]}
              ref={sanctionsDataTableRef}
            />
          </GridItem>
        )}
        {showTable && category === 'terminados' && (
          <GridItem minHeight="300px" xs={12}>
            <DataTable
              headers={[
                { label: 'Licitación asociada', value: 'oc_LIC_Asoc' },
                { label: 'Fecha de inicio contrato', value: 'contractStart' },
                { label: 'Fecha de termino contrato', value: 'contractEnd' },
                { label: 'Tipo de ejecución de contrato', value: 'contractExecutionType' },
                { label: 'Estado del contrato', value: 'contractStatus' }
              ]}
              headersCellsProps={[
                { enableFilter: true, enableSort: true, width: '20%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '15%' },
                { enableFilter: true, enableSort: true, width: '30%' },
                { enableFilter: true, enableSort: true, width: '20%' }
              ]}
              headersDecorators={[
                () => (
                  <>
                    Licitación
                    <br />
                    asociada
                  </>
                ),
                () => (
                  <>
                    Fecha de
                    <br />
                    inicio contrato
                  </>
                ),
                () => (
                  <>
                    Fecha de
                    <br />
                    termino contrato
                  </>
                ),
                () => (
                  <>
                    Tipo de ejecución
                    <br />
                    de contrato
                  </>
                ),
                () => (
                  <>
                    Estado del
                    <br />
                    contrato
                  </>
                )
              ]}
              initialRows={6}
              loading={loading}
              records={tableStructure}
              recordsDecorators={[
                ({ linkContrato, oc_LIC_Asoc: ocLICAsoc }) => (
                  <Link href={linkContrato} rel="noopener noreferrer" target="_blank">
                    {ocLICAsoc}
                  </Link>
                ),
                ({ contractStart }) => contractStart,
                ({ contractEnd }) => contractEnd,
                ({ contractExecutionType }) => contractExecutionType,
                ({ contractStatus }) => contractStatus
              ]}
              ref={sanctionsDataTableRef}
            />
          </GridItem>
        )}
      </GridContainer>
    </Root>
  )
})

ContractFiles.propTypes = {
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onOpen: PropTypes.func.isRequired
}

export default ContractFiles
